import { authorIdsOf, toComment, type WireComment } from './mappers';
import type { CommentsGateway } from '../ports/commentsGateway';

import { getUserAvatarUrlsV2QueryOptions } from '@/services/getUserAvatarUrlsV2';
import { fetchClient } from '@/util/fetchClient';
import { queryClient } from '@/util/queryClient';

/**
 * Shapes wire comments as the thread holds them, with signed avatar URLs.
 *
 * Shared by a thread read and the post read that can carry a post's comments
 * with it: seeding the thread from that response has to produce exactly what
 * fetching it would have. `failFast` skips retrying the avatar lookup, for a
 * caller that has somewhere else to fall back to and should not wait out backoff.
 */
export async function toThread(
  comments: WireComment[],
  { failFast = false }: { failFast?: boolean } = {}
) {
  const userIds = authorIdsOf(comments);

  const avatarUrlsQuery = getUserAvatarUrlsV2QueryOptions(userIds);

  const signedUrls =
    userIds.length > 0
      ? (
          await queryClient.ensureQueryData(
            failFast ? { ...avatarUrlsQuery, retry: false } : avatarUrlsQuery
          )
        ).userAvatarUrlLookup
      : new Map();

  return comments.map((comment) => toComment(comment, signedUrls));
}

/** Comments' backend, over the generated API client. Analytics are the hooks' job, not this. */
export const fetchCommentsGateway: CommentsGateway = {
  async listComments(postId) {
    const { data } = await fetchClient.GET('/v2/comments', {
      params: { query: { postId } },
    });

    if (!data?.data) {
      return [];
    }

    return toThread(data.data);
  },

  async createComment({ postId, body, replyToId, isAnonymous }) {
    await fetchClient.POST('/v2/comments', {
      body: { communityPostId: postId, body, replyToId, isAnonymous },
    });
  },

  async setLiked(commentId, liked) {
    const endpoint = '/v2/comments/{commentId}/likes';

    if (liked) {
      await fetchClient.POST(endpoint, { params: { path: { commentId } } });

      return;
    }

    await fetchClient.DELETE(endpoint, { params: { path: { commentId } } });
  },
};
